// Acessa a variável de ambiente injetada pelo Vite.
// Em desenvolvimento, usar /api para que o proxy do Vite funcione corretamente
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Logs de debug sempre ativos para produção também
console.log('🚀 [API] Inicializando serviço de API');
console.log('🔧 [API] VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
console.log('🔧 [API] API_BASE_URL final:', API_BASE_URL);
console.log('🔧 [API] Ambiente:', import.meta.env.MODE);
console.log('🔧 [API] Dev mode:', import.meta.env.DEV);
console.log('🔧 [API] URL atual:', window.location.href);
console.log('🔧 [API] VITE_PROXY_TARGET:', import.meta.env.VITE_PROXY_TARGET || 'http://localhost:4000');

if (import.meta.env.DEV) {
  console.log('🔍 [API] Modo desenvolvimento ativo');
  console.log('🔍 [API] Proxy configurado para:', import.meta.env.VITE_PROXY_TARGET || 'http://localhost:4000');
}

// Wrapper global para fetch com padronização
export async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const fullUrl = `${API_BASE_URL}${url}`;

  console.log('🔍 [API Fetch] Requisição:', {
    method: options.method || 'GET',
    url: fullUrl,
    body: options.body ? 'Presente' : 'Vazio'
  });

  // Log dos cookies disponíveis
  console.log('🍪 [API Fetch] Cookies disponíveis:', document.cookie);

  // Não definir Content-Type para FormData - o browser define automaticamente
  const isFormData = options.body instanceof FormData;
  const headers = isFormData
    ? { ...(options.headers || {}) }
    : {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include', // 🔑 sempre enviar cookies
      mode: 'cors', // Garantir modo CORS
    });
  } catch (fetchError: any) {
    // Erro de rede (Failed to fetch, CORS, timeout, etc.)
    console.error('❌ [API Fetch] Erro de rede na requisição:', {
      url: fullUrl,
      error: fetchError.message,
      name: fetchError.name,
      stack: fetchError.stack
    });

    // Verificar se é um erro de CORS
    if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
      const error = new Error('Não foi possível conectar ao servidor. Verifique se o backend está rodando e acessível.') as any;
      error.isNetworkError = true;
      error.originalError = fetchError;
      error.url = fullUrl;
      throw error;
    }

    // Outros erros de rede
    const error = new Error(`Erro de conexão: ${fetchError.message}`) as any;
    error.isNetworkError = true;
    error.originalError = fetchError;
    error.url = fullUrl;
    throw error;
  }

  console.log('🔍 [API Fetch] Resposta:', {
    status: response.status,
    statusText: response.statusText,
    url: fullUrl,
    ok: response.ok
  });

  if (!response.ok) {
    let errorData;
    try {
      // Clonar a resposta antes de ler o body
      const responseClone = response.clone();
      // Tentar parsear como JSON primeiro
      errorData = await responseClone.json();
    } catch {
      try {
        // Se não for JSON, tentar ler como texto
        errorData = await response.text();
      } catch {
        // Se falhar completamente, usar mensagem padrão
        errorData = 'Erro desconhecido';
      }
    }

    console.error('❌ [API Fetch] Erro na requisição:', {
      status: response.status,
      statusText: response.statusText,
      url: fullUrl,
      error: errorData
    });

    // Criar erro personalizado que preserva a estrutura da resposta
    // Tentar extrair mensagem de erro do backend se disponível
    let errorMessage = `Erro na requisição: ${response.status} ${response.statusText}`;
    if (errorData) {
      if (typeof errorData === 'object' && errorData.error) {
        errorMessage = errorData.error;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
    }

    const error = new Error(errorMessage) as any;
    error.response = {
      status: response.status,
      statusText: response.statusText,
      data: errorData
    };
    throw error;
  }

  const data = await response.json();
  console.log('✅ [API Fetch] Sucesso:', data);
  return data as T;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface ClientRef {
  id: number;
  legal_name: string;
  trade_name: string;
  account_id?: number;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'cliente' | 'cliente_visual' | 'vendedor' | 'suporte' | 'analista';
  client_id: number | null;
  default_client_id?: number | null;
  allowed_areas?: string[];
  client?: ClientRef;
  clients?: ClientRef[];
}

export interface AuthResponse {
  user: User;
  message: string;
}

// Resposta intermediária do login quando há 2+ contas e nenhum padrão válido.
export interface ClientSelectionResponse {
  needsClientSelection: true;
  clients: ClientRef[];
  preSelectToken: string;
}

export type LoginResult = AuthResponse | ClientSelectionResponse | { firstAccess: true; user: User; message?: string };

class ApiService {
  // Generic HTTP methods usando fetch
  async get<T>(url: string, options?: { params?: any }): Promise<T> {
    try {
      let fullUrl = url;
      if (options?.params) {
        const searchParams = new URLSearchParams(options.params);
        fullUrl = `${url}?${searchParams.toString()}`;
      }
      return await apiFetch<T>(fullUrl, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [API GET] Erro na requisição:', error);
      throw error;
    }
  }

  async post<T>(url: string, data?: any, options?: { headers?: any }): Promise<T> {
    try {
      const isFormData = data instanceof FormData;
      const fetchOptions: RequestInit = {
        method: 'POST',
        body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      };

      if (options?.headers) {
        fetchOptions.headers = { ...fetchOptions.headers, ...options.headers };
      }

      return await apiFetch<T>(url, fetchOptions);
    } catch (error: any) {
      console.error('❌ [API POST] Erro na requisição:', error);
      throw error;
    }
  }

  async put<T>(url: string, data?: any): Promise<T> {
    try {
      return await apiFetch<T>(url, {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      });
    } catch (error: any) {
      console.error('❌ [API PUT] Erro na requisição:', error);
      throw error;
    }
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    try {
      return await apiFetch<T>(url, {
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
      });
    } catch (error: any) {
      console.error('❌ [API PATCH] Erro na requisição:', error);
      throw error;
    }
  }

  async delete<T>(url: string): Promise<T> {
    try {
      return await apiFetch<T>(url, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [API DELETE] Erro na requisição:', error);
      throw error;
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResult> {
    try {
      return await apiFetch<LoginResult>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
    } catch (error: any) {
      console.error('❌ [API Login] Erro:', error);
      throw error;
    }
  }

  async me(): Promise<{ user: User }> {
    try {
      return await apiFetch<{ user: User }>('/auth/me');
    } catch (error: any) {
      console.error('❌ [API Me] Erro:', error);
      throw error;
    }
  }

  // Define/troca o cliente ativo. No login multi-conta, passa o preSelectToken (Bearer).
  async setActiveClient(
    clientId: number,
    opts?: { setAsDefault?: boolean; clearDefault?: boolean; preSelectToken?: string }
  ): Promise<AuthResponse> {
    try {
      const headers: Record<string, string> = {};
      if (opts?.preSelectToken) {
        headers['Authorization'] = `Bearer ${opts.preSelectToken}`;
      }
      return await apiFetch<AuthResponse>('/auth/active-client', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          client_id: clientId,
          set_as_default: opts?.setAsDefault ?? false,
          clear_default: opts?.clearDefault ?? false,
        }),
      });
    } catch (error: any) {
      console.error('❌ [API setActiveClient] Erro:', error);
      throw error;
    }
  }

  async logout(): Promise<{ message: string }> {
    try {
      return await apiFetch<{ message: string }>('/auth/logout', {
        method: 'POST',
      });
    } catch (error: any) {
      console.error('❌ [API Logout] Erro:', error);
      throw error;
    }
  }

  async register(userData: {
    name: string;
    email: string;
    password: string;
    role: 'admin' | 'cliente';
    client_id?: number;
  }): Promise<{ user: User; message: string }> {
    try {
      return await apiFetch<{ user: User; message: string }>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    } catch (error: any) {
      console.error('❌ [API Register] Erro:', error);
      throw error;
    }
  }

  async getUsers(): Promise<{ users: User[] }> {
    try {
      return await apiFetch<{ users: User[] }>('/auth/users');
    } catch (error: any) {
      console.error('❌ [API GetUsers] Erro:', error);
      throw error;
    }
  }

  /**
   * Lista log de auditoria com filtros e paginação.
   * Admin: pode filtrar por client_id. Cliente: só vê seus próprios logs.
   */
  async getAuditLogs(params: {
    client_id?: number;
    user_id?: number;
    action?: string;
    resource_type?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; list: any[]; total: number; page: number; limit: number; totalPages: number }> {
    try {
      const q: Record<string, string> = {};
      if (params.client_id != null) q.client_id = String(params.client_id);
      if (params.user_id != null) q.user_id = String(params.user_id);
      if (params.action) q.action = params.action;
      if (params.resource_type) q.resource_type = params.resource_type;
      if (params.from) q.from = params.from;
      if (params.to) q.to = params.to;
      if (params.page != null) q.page = String(params.page);
      if (params.limit != null) q.limit = String(params.limit);
      return await this.get<{ success: boolean; list: any[]; total: number; page: number; limit: number; totalPages: number }>(
        '/audit-logs',
        { params: q }
      );
    } catch (error: any) {
      console.error('❌ [API getAuditLogs] Erro:', error);
      throw error;
    }
  }

  async getClients(): Promise<any[]> {
    try {
      const data = await apiFetch<{ success: boolean; clients: any[] }>('/clients');

      // O backend retorna { success: true, clients: [...] }
      // Precisamos extrair apenas o array de clientes
      if (data.clients && Array.isArray(data.clients)) {
        console.log('✅ [API GetClients] Clientes extraídos:', data.clients);
        return data.clients;
      }

      // Se por algum motivo não tiver a estrutura esperada, retornar array vazio
      if (import.meta.env.DEV) {
        console.warn('⚠️ [API GetClients] Estrutura de dados inesperada:', data);
      }
      return [];
    } catch (error: any) {
      console.error('❌ [API GetClients] Erro:', error);
      throw error;
    }
  }

  async createClient(clientData: any): Promise<any> {
    try {
      return await apiFetch<any>('/clients', {
        method: 'POST',
        body: JSON.stringify(clientData),
      });
    } catch (error: any) {
      console.error('❌ [API CreateClient] Erro:', error);
      throw error;
    }
  }

  async updateClient(id: number, clientData: any): Promise<any> {
    try {
      return await apiFetch<any>(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(clientData),
      });
    } catch (error: any) {
      console.error('❌ [API UpdateClient] Erro:', error);
      throw error;
    }
  }

  async deleteClient(id: number): Promise<void> {
    try {
      await apiFetch<void>(`/clients/${id}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [API DeleteClient] Erro:', error);
      throw error;
    }
  }

  /**
   * Lista cobranças Asaas do cliente (requer client.asaas_id).
   */
  async getClientAsaasPayments(
    clientId: number,
    params?: { offset?: number; limit?: number }
  ): Promise<{
    data: Array<{
      id: string;
      description: string | null;
      value: number;
      netValue: number | null;
      status: string;
      dueDate: string;
      paymentDate: string | null;
      bankSlipUrl: string | null;
      invoiceNumber: string | null;
      billingType: string;
      [key: string]: unknown;
    }>;
    totalCount: number;
    hasMore: boolean;
    offset: number;
    limit: number;
  }> {
    const sp = new URLSearchParams();
    if (params?.offset != null) sp.set('offset', String(params.offset));
    if (params?.limit != null) sp.set('limit', String(params.limit));
    const query = sp.toString();
    const url = `/clients/${clientId}/asaas/payments${query ? `?${query}` : ''}`;
    return apiFetch(url);
  }

  /**
   * Envia boleto por e-mail para o e-mail cadastrado do cliente.
   * Body: { paymentId: "pay_xxx" } ou { bankSlipUrl: "https://...", description?: "..." }
   */
  async sendClientPaymentEmail(
    clientId: number,
    payload: { paymentId?: string; bankSlipUrl?: string; description?: string }
  ): Promise<{ success: boolean; message?: string }> {
    return apiFetch(`/clients/${clientId}/asaas/send-payment-email`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Atualiza cobrança Asaas (valor, descrição, data de vencimento).
   * Parâmetros omitidos mantêm o valor atual.
   */
  async updateClientAsaasPayment(
    clientId: number,
    paymentId: string,
    payload: { value?: number; dueDate?: string; description?: string }
  ): Promise<Record<string, unknown>> {
    return apiFetch(`/clients/${clientId}/asaas/payments/${encodeURIComponent(paymentId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Busca o ID do ÁtomosChat (v2) na API e salva no cliente.
   * Faz match do documentId da API com company_tax_id do cliente.
   */
  async fetchAtomosChatAccountId(clientId: number): Promise<{ success: boolean; atomos_chat_account_id: string }> {
    return apiFetch(`/clients/${clientId}/fetch-atomos-chat-account-id`, {
      method: 'POST',
    });
  }

  /**
   * Busca dados da empresa pela API OpenCNPJ (CNPJ apenas dígitos).
   */
  async fetchCompanyByCnpj(cnpj: string): Promise<Partial<import('../types/client').Client>> {
    const digits = (cnpj || '').replace(/\D/g, '');
    if (digits.length !== 14) {
      throw new Error('CNPJ deve ter 14 dígitos');
    }
    return apiFetch(`/cnpj/${digits}`);
  }

  /**
   * Download de arquivo como blob
   */
  async downloadFile(url: string): Promise<Blob> {
    try {
      const fullUrl = `${API_BASE_URL}${url}`;

      console.log('🔍 [API Download] Requisição:', fullUrl);

      const response = await fetch(fullUrl, {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('✅ [API Download] Sucesso:', blob.size, 'bytes');
      return blob;
    } catch (error: any) {
      console.error('❌ [API Download] Erro:', error);
      throw error;
    }
  }

  // Blocked Contacts API
  // 🔧 CORREÇÃO: Remover /api das URLs pois API_BASE_URL já inclui /api em produção
  async getBlockedContacts(clientId: number) {
    try {
      return await this.get<import('../types/blockedContact').BlockedContactListResponse>(
        `/clients/${clientId}/blocked-contacts`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao buscar contatos bloqueados:', error);
      throw error;
    }
  }

  async addBlockedContact(clientId: number, data: import('../types/blockedContact').BlockedContactCreateRequest) {
    try {
      return await this.post<import('../types/blockedContact').BlockedContactCreateResponse>(
        `/clients/${clientId}/blocked-contacts`,
        data
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao adicionar contato bloqueado:', error);
      throw error;
    }
  }

  async updateBlockedContact(
    clientId: number,
    contactId: number,
    data: import('../types/blockedContact').BlockedContactUpdateRequest
  ) {
    try {
      return await this.put<import('../types/blockedContact').BlockedContactUpdateResponse>(
        `/clients/${clientId}/blocked-contacts/${contactId}`,
        data
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao atualizar contato bloqueado:', error);
      throw error;
    }
  }

  async removeBlockedContact(clientId: number, contactId: number) {
    try {
      return await this.delete<import('../types/blockedContact').BlockedContactDeleteResponse>(
        `/clients/${clientId}/blocked-contacts/${contactId}`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao remover contato bloqueado:', error);
      throw error;
    }
  }

  async getBlockedVehicles(clientId: number) {
    try {
      return await this.get<import('../types/blockedVehicle').BlockedVehicleListResponse>(
        `/clients/${clientId}/blocked-vehicles`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao buscar veículos bloqueados:', error);
      throw error;
    }
  }

  async addBlockedVehicle(clientId: number, data: import('../types/blockedVehicle').BlockedVehicleCreateRequest) {
    try {
      return await this.post<import('../types/blockedVehicle').BlockedVehicleCreateResponse>(
        `/clients/${clientId}/blocked-vehicles`,
        data
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao adicionar veículo bloqueado:', error);
      throw error;
    }
  }

  async updateBlockedVehicle(
    clientId: number,
    vehicleId: number,
    data: import('../types/blockedVehicle').BlockedVehicleUpdateRequest
  ) {
    try {
      return await this.put<import('../types/blockedVehicle').BlockedVehicleUpdateResponse>(
        `/clients/${clientId}/blocked-vehicles/${vehicleId}`,
        data
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao atualizar veículo bloqueado:', error);
      throw error;
    }
  }

  async removeBlockedVehicle(clientId: number, vehicleId: number) {
    try {
      return await this.delete<import('../types/blockedVehicle').BlockedVehicleDeleteResponse>(
        `/clients/${clientId}/blocked-vehicles/${vehicleId}`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao remover veículo bloqueado:', error);
      throw error;
    }
  }

  // Export Files API
  async requestExport(fileType: 'excel' | 'pdf', filters: any) {
    try {
      return await this.post<{ success: boolean; data: { id: number; status: string; file_type: string; created_at: string }; message: string }>(
        '/attendances/export/request',
        { file_type: fileType, ...filters }
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao solicitar exportação:', error);
      throw error;
    }
  }

  async listExportFiles(params?: { limit?: number; offset?: number; status?: string; file_type?: string }) {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.offset) queryParams.append('offset', String(params.offset));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.file_type) queryParams.append('file_type', params.file_type);
      
      const query = queryParams.toString();
      return await this.get<{ success: boolean; data: { files: any[]; total: number; limit: number; offset: number } }>(
        `/attendances/export/files${query ? `?${query}` : ''}`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao listar arquivos exportados:', error);
      throw error;
    }
  }

  async getExportProgress(id: number) {
    try {
      return await this.get<{ success: boolean; data: { export_file_id: number; job_id?: string; progress: number; status: string; state: string; failed_reason?: string | null } }>(
        `/attendances/export/progress/${id}`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao obter progresso da exportação:', error);
      throw error;
    }
  }

  async downloadExportFile(id: number) {
    try {
      return await this.get<{ success: boolean; data: { download_url: string; file_name: string; file_size: number; expires_in: number } }>(
        `/attendances/export/download/${id}`
      );
    } catch (error: any) {
      console.error('❌ [API] Erro ao obter URL de download:', error);
      throw error;
    }
  }

  // Disparo Avulso (XLSX + Meta + ChatConnect)
  async disparoAvulsoPreviewXlsx(formData: FormData) {
    return await this.post<{
      success: boolean;
      headers: string[];
      totalRows: number;
      sampleRows: Record<string, unknown>[];
      expectedVariables: number;
      suggestedMapping: Record<string, number>;
      templateHeader: { hasHeader: boolean; type?: 'image' | 'video' | 'document' };
      templateBodyText?: string | null;
    }>('/disparos-avulsos/preview-xlsx', formData);
  }

  async disparoAvulsoCreate(formData: FormData) {
    return await this.post<{
      success: boolean;
      disparo: any;
      created: number;
      errors: string[];
    }>('/disparos-avulsos', formData);
  }

  async disparoAvulsoList(params?: {
    client_id?: number;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    return await this.get<{ success: boolean; list: any[]; total: number }>(
      '/disparos-avulsos',
      params && Object.keys(params).length ? { params: params as any } : undefined
    );
  }

  async disparoAvulsoGetById(id: number, clientId?: number) {
    const params = clientId != null ? { client_id: clientId } : undefined;
    return await this.get<{ success: boolean; disparo: any }>(`/disparos-avulsos/${id}`, params ? { params } : undefined);
  }

  async disparoAvulsoGetContatos(id: number, page = 1, limit = 50, clientId?: number) {
    const params: Record<string, number> = { page, limit };
    if (clientId != null) params.client_id = clientId;
    return await this.get<{
      success: boolean;
      disparo: any;
      contatos: any[];
      total: number;
      page: number;
      limit: number;
    }>(`/disparos-avulsos/${id}/contatos`, { params });
  }

  async disparoAvulsoIniciar(id: number, clientId: number) {
    return await this.post<{ success: boolean; disparo: any; message: string }>(
      `/disparos-avulsos/${id}/iniciar`,
      { client_id: clientId }
    );
  }

  async disparoAvulsoCancelar(id: number, clientId: number) {
    return await this.post<{ success: boolean; disparo: any; message: string }>(
      `/disparos-avulsos/${id}/cancelar`,
      { client_id: clientId }
    );
  }

  async disparoAvulsoPausar(id: number, clientId: number) {
    return await this.post<{ success: boolean; disparo: any; message: string }>(
      `/disparos-avulsos/${id}/pausar`,
      { client_id: clientId }
    );
  }

  async disparoAvulsoRetomar(id: number, clientId: number) {
    return await this.post<{ success: boolean; disparo: any; message: string }>(
      `/disparos-avulsos/${id}/retomar`,
      { client_id: clientId }
    );
  }

  async disparoAvulsoReenviarFalhas(id: number, clientId: number) {
    return await this.post<{ success: boolean; disparo: any; message: string }>(
      `/disparos-avulsos/${id}/reenviar-falhas`,
      { client_id: clientId }
    );
  }

  // Central de Cobrança (importação pode levar 1–2 min; timeout de 5 min)
  async cobrancaImport(body: { dueDateGe: string; dueDateLe: string; status: string }) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);
    try {
      return await apiFetch<{ message: string; lote: any; itens: any[] }>('/cobranca/import', {
        method: 'POST',
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async cobrancaListLotes() {
    return await this.get<any[]>('/cobranca/lotes');
  }

  async cobrancaGetLote(id: number) {
    return await this.get<{ lote: any; itens: any[] }>(`/cobranca/lotes/${id}`);
  }

  async cobrancaMarcarItemPago(itemId: number, dataPagamento: string) {
    return await apiFetch<{ message: string; item: any }>(`/cobranca/itens/${itemId}/marcar-pago`, {
      method: 'PATCH',
      body: JSON.stringify({ data_pagamento: dataPagamento }),
    });
  }

  async cobrancaDeleteLote(id: number, password: string) {
    return await apiFetch<{ message: string }>(`/cobranca/lotes/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  }

  async cobrancaCreateContato(
    itemId: number,
    body: {
      contact_type: string;
      contact_status: string;
      observation?: string;
      promessa_pagamento?: boolean;
      promessa_data?: string;
    }
  ) {
    return await this.post<{ contato: any }>(`/cobranca/itens/${itemId}/contatos`, body);
  }

  async cobrancaGetContatos(itemId: number) {
    return await this.get<any[]>(`/cobranca/itens/${itemId}/contatos`);
  }
}

const api = new ApiService();
export default api;
export const apiService = api;